import { supabase } from '../lib/supabase';
import { calculateLinearRegression, DEFAULT_PH_CALIBRATION } from '../utils/sensorConversions';
/**
 * Extract PHCalibration parameters from a DeviceTest
 */
export function extractCalibrationFromTest(test) {
    if (test.test_type === 'three_point_calibration') {
        return {
            sensitivityMvPerPh: test.data.sensitivity_mv_per_ph,
            offsetMv: test.data.offset_mv
        };
    }
    else {
        // 1-point calibration: compute offset using default slope
        // The stored offset_mv is the voltage at the buffer pH
        // We need to compute offset at pH 0: offset = voltage - (slope * bufferPh)
        const bufferPh = test.data.buffer.ph;
        const bufferVoltage = test.data.buffer.voltage_mv;
        const offsetAtPh0 = bufferVoltage - DEFAULT_PH_CALIBRATION.sensitivityMvPerPh * bufferPh;
        return {
            sensitivityMvPerPh: DEFAULT_PH_CALIBRATION.sensitivityMvPerPh,
            offsetMv: offsetAtPh0
        };
    }
}
export class DeviceTestService {
    /**
     * Get all tests for a specific device
     */
    static async getDeviceTests(deviceId) {
        const { data, error } = await supabase
            .from('device_tests')
            .select('*')
            .eq('device_id', deviceId)
            .order('created_at', { ascending: false });
        if (error) {
            console.error('Error fetching device tests:', error);
            throw new Error(error.message);
        }
        return data || [];
    }
    /**
     * Create a new device test with calculated calibration values
     */
    static async createDeviceTest(deviceId, testType, inputData) {
        // Calculate calibration values based on test type
        let data;
        if (testType === 'three_point_calibration' && 'buffers' in inputData) {
            const buffers = inputData.buffers;
            const points = buffers.map(b => ({ x: b.ph, y: b.voltage_mv }));
            const regression = calculateLinearRegression(points);
            data = {
                buffers,
                sensitivity_mv_per_ph: regression.slope,
                offset_mv: regression.yIntercept,
                linearity_r_squared: regression.rSquared
            };
        }
        else if (testType === 'one_point_calibration' && 'buffer' in inputData) {
            data = {
                buffer: inputData.buffer,
                offset_mv: inputData.buffer.voltage_mv
            };
        }
        else {
            throw new Error('Invalid test type or data combination');
        }
        const { data: result, error } = await supabase
            .from('device_tests')
            .insert({
            device_id: deviceId,
            test_type: testType,
            data: data
        })
            .select()
            .single();
        if (error) {
            console.error('Error creating device test:', error);
            throw new Error(error.message);
        }
        return result;
    }
    /**
     * Check if a device has any calibration tests
     */
    static async hasCalibrationTest(deviceId) {
        const { data } = await supabase
            .from('device_tests')
            .select('id')
            .eq('device_id', deviceId)
            .in('test_type', ['three_point_calibration', 'one_point_calibration'])
            .limit(1);
        return (data?.length ?? 0) > 0;
    }
    /**
     * Get the latest calibration for a device
     * Returns the most recent calibration test, or default values if none exists
     */
    static async getLatestCalibration(deviceId) {
        const { data, error } = await supabase
            .from('device_tests')
            .select('*')
            .eq('device_id', deviceId)
            .in('test_type', ['three_point_calibration', 'one_point_calibration'])
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
        if (error) {
            console.error('Error fetching latest calibration:', error);
            return { calibration: DEFAULT_PH_CALIBRATION, source: 'default' };
        }
        if (!data) {
            return { calibration: DEFAULT_PH_CALIBRATION, source: 'default' };
        }
        const test = data;
        return {
            calibration: extractCalibrationFromTest(test),
            source: test.test_type
        };
    }
}
